import React, { useState, useEffect, useRef, useCallback } from "react";

/**
 * DownloadGurSetuPage.jsx
 * -----------------------------------------------------------------------
 * GurSetu — Gurdwara & Seva community app — Play Store download page.
 *
 * WHAT THIS FILE GIVES YOU
 *  - A full, self-contained React page (default export: DownloadGurSetuPage)
 *  - Trilingual UI: English / हिंदी / ਪੰਜਾਬੀ, switchable via pill toggle
 *  - Signature "blur-morph" headline effect that cycles lines per language
 *  - Animated arch-framed QR panel (click => ripple + redirect to Play Store)
 *  - Animated Play Store button (click => same ripple + redirect sequence)
 *  - Orchestrated load-in sequence, reduced-motion support, keyboard focus
 *
 * HOW TO WIRE THIS INTO YOUR APP (React Router project)
 * -----------------------------------------------------------------------
 * 1) Place this file at:  src/pages/DownloadGurSetuPage.jsx
 *    (or src/components/DownloadGurSetuPage.jsx — see App.js note below)
 *
 * 2) Put the QR image at:
 *    public/assets/images/gursetu-playstore-qr.png
 *    (You already saved it there — the <img> below points at
 *     "/assets/images/gursetu-playstore-qr.png", which is how you
 *     reference anything inside `public/` with Create-React-App or Vite.)
 *
 * 3) Set your REAL Play Store link in PLAY_STORE_URL below.
 *
 * 4) App.js wiring (react-router-dom v6):
 *
 *      import { BrowserRouter, Routes, Route } from "react-router-dom";
 *      import DownloadGurSetuPage from "./pages/DownloadGurSetuPage";
 *
 *      function App() {
 *        return (
 *          <BrowserRouter>
 *            <Routes>
 *              <Route path="/" element={<Home />} />
 *              <Route path="/download" element={<DownloadGurSetuPage />} />
 *            </Routes>
 *          </BrowserRouter>
 *        );
 *      }
 *
 * 5) Sidebar wiring — wherever your sidebar/nav links live (e.g.
 *    src/components/Sidebar.jsx), add a Link pointing at "/download":
 *
 *      import { Link } from "react-router-dom";
 *      <Link to="/download" className="sidebar-link">
 *        📲 Download App
 *      </Link>
 *
 *    If your sidebar is a plain <a> based menu instead of react-router,
 *    just use <a href="/download">Download App</a>.
 *
 * 6) No extra CSS files needed — all styles are scoped inline in a
 *    <style> tag injected once by this component, using a unique
 *    prefix ("gs-") so it won't collide with the rest of your app.
 * -----------------------------------------------------------------------
 */

// ⬇️ Put your real Play Store listing URL here.
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.gursetu.app";

const QR_IMAGE_SRC = "/assets/images/gursetu-playstore-qr.png";

/* -------------------------------------------------------------------- */
/*  Language content                                                     */
/* -------------------------------------------------------------------- */

const LANGS = ["en", "hi", "pa"];

const LANG_LABEL = {
  en: "EN",
  hi: "हिं",
  pa: "ਪੰ",
};

const LANG_FULL = {
  en: "English",
  hi: "हिंदी",
  pa: "ਪੰਜਾਬੀ",
};

// Each language has a short sequence of headline phrases that the
// blur-morph effect cycles through, ending on a settled final line.
const HEADLINE_SEQUENCE = {
  en: [
    "Your spiritual companion is here",
    "Live gurdwara timings at a glance",
    "Access many more personal spiritual features with just two taps.",
    "Begin every morning with Hukamnama",
    "Download GurSetu today",
  ],
  hi: [
    "आपका आध्यात्मिक साथी आ गया है",
    "लाइव गुरुद्वारा समय अब एक नज़र में",
    "बस दो टैप में कई और पर्सनल आध्यात्मिक फ़ीचर्स।",
    "हर सुबह हुकमनामा के साथ शुरुआत करें",
    "आज ही GurSetu डाउनलोड करें",
  ],
  pa: [
    "ਤੁਹਾਡਾ ਆਤਮਿਕ ਸਾਥੀ ਆ ਗਿਆ ਹੈ",
    "ਲਾਈਵ ਗੁਰਦੁਆਰਾ ਸਮਾਂ ਹੁਣ ਇੱਕ ਨਜ਼ਰ ਵਿੱਚ",
    "ਸਿਰਫ਼ ਦੋ ਟੈਪਾਂ ਵਿੱਚ ਹੋਰ ਵੀ ਬਹੁਤ ਸਾਰੀਆਂ ਨਿੱਜੀ ਅਧਿਆਤਮਿਕ ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ।",
    "ਹਰ ਸਵੇਰ ਹੁਕਮਨਾਮੇ ਨਾਲ ਦਿਨ ਸ਼ੁਰੂ ਕਰੋ",
    "ਅੱਜ ਹੀ GurSetu ਡਾਊਨਲੋਡ ਕਰੋ",
  ],
};

const COPY = {
  en: {
    eyebrow: "Daily spirituality, beautifully connected",
    sub: "From live gurdwara timings to daily Hukamnama, GurSetu helps you stay connected with faith, sangat and seva — every single day.",
    cta: "Download GurSetu on Google Play",
    qrTitle: "Scan to enter GurSetu",
    qrSub: "Point your camera at the QR or tap the button below",
    orDivider: "or",
    highlights: [
      "Live sangat updates",
      "Daily spiritual reminders",
      "Access many more personal spiritual features with just two taps.",
      "Hukamnama in your language",
      "Scan. Tap. Begin.",
    ],
    features: [
      {
        title: "Live Gurdwara Timings",
        body: "See Nitnem, Rehras, Ardas and local gurdwara timings refreshed in real time, so you never miss the moment.",
        pill: "LIVE",
        mini: "Real-time • Local • Reliable",
      },
      {
        title: "Many more personal spiritual features ",
        body: "Access many more personal spiritual features with just two taps.",
        pill: "FEATURES",
        mini: "2 taps • Instant • Simple",
      },
      {
        title: "Daily Hukamnama",
        body: "Start your day with Hukamnama Sahib, along with audio and translation in the language you understand best.",
        pill: "DAILY",
        mini: "Audio • Translation • Peace",
      },
    ],
    footerTag: "Built with sangat, for sangat.",
    scanLabel: "SCAN NOW",
  },

  hi: {
    eyebrow: "रोज़ की आध्यात्मिकता, अब और भी जुड़ी हुई",
    sub: "लाइव गुरुद्वारा समय, सेवा साइन-अप और रोज़ाना हुकमनामा — GurSetu आपको आस्था, संगत और सेवा से हर दिन जोड़े रखता है।",
    cta: "Google Play पर GurSetu डाउनलोड करें",
    qrTitle: "GurSetu में आने के लिए स्कैन करें",
    qrSub: "कैमरा QR पर रखें या नीचे दिया बटन टैप करें",
    orDivider: "या",
    highlights: [
      "लाइव संगत अपडेट्स",
      "रोज़ के आध्यात्मिक रिमाइंडर",
      "बस दो टैप में कई और पर्सनल आध्यात्मिक फ़ीचर्स।",
      "आपकी भाषा में हुकमनामा",
      "स्कैन करें। टैप करें। शुरू करें।",
    ],
    features: [
      {
        title: "लाइव गुरुद्वारा समय",
        body: "नितनेम, रहरास, अरदास और आपके स्थानीय गुरुद्वारे के समय अब रियल टाइम में, ताकि आप कुछ भी मिस न करें।",
        pill: "लाइव",
        mini: "रियल टाइम • लोकल • भरोसेमंद",
      },
      {
        title: "बस दो टैप ",
        body: "बस दो टैप में कई और पर्सनल आध्यात्मिक फ़ीचर्स।",
        pill: "विशेषताएँ",
        mini: "2 टैप • तुरंत • आसान",
      },
      {
        title: "रोज़ाना हुकमनामा",
        body: "हर सुबह हुकमनामा साहिब के साथ दिन शुरू करें — ऑडियो और अनुवाद सहित, आपकी पसंदीदा भाषा में।",
        pill: "रोज़ाना",
        mini: "ऑडियो • अनुवाद • शांति",
      },
    ],
    footerTag: "संगत के साथ, संगत के लिए बना।",
    scanLabel: "अभी स्कैन करें",
  },

  pa: {
    eyebrow: "ਰੋਜ਼ਾਨਾ ਰੂਹਾਨੀ ਜੁੜਾਵ, ਹੁਣ ਹੋਰ ਸੁੰਦਰ ਢੰਗ ਨਾਲ",
    sub: "ਲਾਈਵ ਗੁਰਦੁਆਰਾ ਸਮੇਂ, ਸੇਵਾ ਸਾਈਨ-ਅੱਪ ਅਤੇ ਰੋਜ਼ਾਨਾ ਹੁਕਮਨਾਮੇ ਨਾਲ GurSetu ਤੁਹਾਨੂੰ ਹਰ ਦਿਨ ਵਿਸ਼ਵਾਸ, ਸੰਗਤ ਅਤੇ ਸੇਵਾ ਨਾਲ ਜੋੜ ਕੇ ਰੱਖਦਾ ਹੈ।",
    cta: "Google Play ਤੋਂ GurSetu ਡਾਊਨਲੋਡ ਕਰੋ",
    qrTitle: "GurSetu ਵਿੱਚ ਜਾਣ ਲਈ ਸਕੈਨ ਕਰੋ",
    qrSub: "ਆਪਣਾ ਕੈਮਰਾ QR ਕੋਡ ਵੱਲ ਕਰੋ ਜਾਂ ਹੇਠਾਂ ਬਟਨ ਦਬਾਓ",
    orDivider: "ਜਾਂ",
    highlights: [
      "ਲਾਈਵ ਸੰਗਤ ਅੱਪਡੇਟਸ",
      "ਰੋਜ਼ਾਨਾ ਆਤਮਿਕ ਰਿਮਾਈਂਡਰ",
      "ਸਿਰਫ਼ ਦੋ ਟੈਪਾਂ ਵਿੱਚ ਹੋਰ ਵੀ ਬਹੁਤ ਸਾਰੀਆਂ ਨਿੱਜੀ ਅਧਿਆਤਮਿਕ ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ।",
      "ਤੁਹਾਡੀ ਭਾਸ਼ਾ ਵਿੱਚ ਹੁਕਮਨਾਮਾ",
      "ਸਕੈਨ ਕਰੋ। ਟੈਪ ਕਰੋ। ਸ਼ੁਰੂ ਕਰੋ।",
    ],
    features: [
      {
        title: "ਲਾਈਵ ਗੁਰਦੁਆਰਾ ਸਮੇਂ",
        body: "ਨਿਤਨੇਮ, ਰਹਿਰਾਸ, ਅਰਦਾਸ ਅਤੇ ਤੁਹਾਡੇ ਨੇੜਲੇ ਗੁਰਦੁਆਰੇ ਦੇ ਸਮੇਂ ਹੁਣ ਰੀਅਲ ਟਾਈਮ ਵਿੱਚ, ਤਾਂ ਜੋ ਤੁਸੀਂ ਕੋਈ ਵੀ ਪਲ ਨਾ ਗੁਆਓ।",
        pill: "ਲਾਈਵ",
        mini: "ਰੀਅਲ ਟਾਈਮ • ਲੋਕਲ • ਭਰੋਸੇਯੋਗ",
      },
      {
        title: "ਸਿਰਫ਼ ਦੋ ਟੈਪ ",
        body: "ਸਿਰਫ਼ ਦੋ ਟੈਪਾਂ ਵਿੱਚ ਹੋਰ ਵੀ ਬਹੁਤ ਸਾਰੀਆਂ ਨਿੱਜੀ ਅਧਿਆਤਮਿਕ ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ।",
        pill: "ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ",
        mini: "2 ਟੈਪ • ਤੁਰੰਤ • ਸੌਖਾ",
      },
      {
        title: "ਰੋਜ਼ਾਨਾ ਹੁਕਮਨਾਮਾ",
        body: "ਹਰ ਸਵੇਰ ਹੁਕਮਨਾਮਾ ਸਾਹਿਬ, ਆਡੀਓ ਅਤੇ ਅਨੁਵਾਦ ਦੇ ਨਾਲ — ਤੁਹਾਡੀ ਮਨਪਸੰਦ ਭਾਸ਼ਾ ਵਿੱਚ।",
        pill: "ਰੋਜ਼ਾਨਾ",
        mini: "ਆਡੀਓ • ਅਨੁਵਾਦ • ਸ਼ਾਂਤੀ",
      },
    ],
    footerTag: "ਸੰਗਤ ਨਾਲ, ਸੰਗਤ ਲਈ ਬਣਾਇਆ ਗਿਆ।",
    scanLabel: "ਹੁਣ ਸਕੈਨ ਕਰੋ",
  },
};

/* -------------------------------------------------------------------- */
/*  Blur-morph headline effect                                          */
/* -------------------------------------------------------------------- */
/**
 * Cycles through HEADLINE_SEQUENCE[lang]:
 *   settled -> blur+fade out -> swap text -> blur+fade in -> settled -> ...
 * Restarts whenever `lang` changes. Respects prefers-reduced-motion by
 * skipping straight to the final line with no blur animation.
 */
function useBlurMorphHeadline(lang, prefersReducedMotion) {
  const lines = HEADLINE_SEQUENCE[lang] || HEADLINE_SEQUENCE.en;
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("in"); // 'in' | 'settled' | 'out'
  const timeoutsRef = useRef([]);

  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
  }, []);

  useEffect(() => {
    clearTimers();
    setIndex(0);

    if (prefersReducedMotion) {
      setPhase("settled");
      setIndex(lines.length - 1);
      return () => clearTimers();
    }

    setPhase("in");

    const HOLD = 1500; // ms a line stays fully sharp
    const TRANSITION = 500; // ms of blur transition

    function schedule(i) {
      // become settled shortly after entering
      const t1 = setTimeout(() => setPhase("settled"), TRANSITION);
      // start blurring out after hold, if not last line
      if (i < lines.length - 1) {
        const t2 = setTimeout(() => setPhase("out"), TRANSITION + HOLD);
        const t3 = setTimeout(() => {
          setIndex(i + 1);
          setPhase("in");
          schedule(i + 1);
        }, TRANSITION + HOLD + TRANSITION);
        timeoutsRef.current.push(t2, t3);
      }
      timeoutsRef.current.push(t1);
    }

    schedule(0);
    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, prefersReducedMotion]);

  return { text: lines[index], phase, isLast: index === lines.length - 1 };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener
        ? mq.removeEventListener("change", handler)
        : mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

/* -------------------------------------------------------------------- */
/*  Redirect-with-animation helper                                      */
/* -------------------------------------------------------------------- */
/**
 * Fires a short ripple/scale burst, then navigates to the Play Store
 * after the animation has had time to read. `origin` is a {x,y} in
 * viewport pixels used to position the ripple.
 */
function useLaunchSequence() {
  const [burst, setBurst] = useState(null); // {x, y, key}
  const redirectTimer = useRef(null);

  const launch = useCallback((clientX, clientY) => {
    const key = Date.now();
    setBurst({ x: clientX, y: clientY, key });

    if (redirectTimer.current) clearTimeout(redirectTimer.current);
    redirectTimer.current = setTimeout(() => {
      window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
      setBurst(null);
    }, 620);
  }, []);

  useEffect(() => () => {
    if (redirectTimer.current) clearTimeout(redirectTimer.current);
  }, []);

  return { burst, launch };
}

/* -------------------------------------------------------------------- */
/*  Small presentational bits                                           */
/* -------------------------------------------------------------------- */

function ArchFrame({ children }) {
  // The recurring structural motif: a gurdwara-arch outline framing
  // the QR code, echoed faintly behind the hero too.
  return (
    <div className="gs-arch-frame">
      <svg
        className="gs-arch-svg"
        viewBox="0 0 320 360"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M10 356 V150 A150 150 0 0 1 310 150 V356"
          fill="none"
          stroke="url(#gsArchGrad)"
          strokeWidth="3"
        />
        <path
          d="M28 356 V150 A132 132 0 0 1 292 150 V356"
          fill="none"
          stroke="url(#gsArchGrad)"
          strokeWidth="1"
          opacity="0.5"
        />
        <circle cx="160" cy="34" r="6" fill="url(#gsArchGrad)" />
        <path d="M160 40 V16" stroke="url(#gsArchGrad)" strokeWidth="2" />
        <defs>
          <linearGradient id="gsArchGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F0C05A" />
            <stop offset="100%" stopColor="#C9973E" />
          </linearGradient>
        </defs>
      </svg>
      <div className="gs-arch-content">{children}</div>
    </div>
  );
}

function LaunchBurst({ burst }) {
  if (!burst) return null;
  return (
    <div
      className="gs-burst"
      style={{ left: burst.x, top: burst.y }}
      key={burst.key}
      aria-hidden="true"
    >
      <span className="gs-burst-ring gs-burst-ring--1" />
      <span className="gs-burst-ring gs-burst-ring--2" />
      <span className="gs-burst-ring gs-burst-ring--3" />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Main component                                                      */
/* -------------------------------------------------------------------- */

export default function DownloadGurSetuPage() {
  const [lang, setLang] = useState("en");
  const [loaded, setLoaded] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { text: headlineText, phase } = useBlurMorphHeadline(
    lang,
    prefersReducedMotion
  );
  const { burst, launch } = useLaunchSequence();
  const t = COPY[lang];
  const scriptClass = `gs-script-${lang}`;

  useEffect(() => {
    // Orchestrated single load-in sequence (not per-section scroll spam).
    const id = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleQrClick = (e) => {
    launch(e.clientX, e.clientY);
  };

  const handleQrKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      launch(rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  };

  const handleCtaClick = (e) => {
    launch(e.clientX, e.clientY);
  };

  return (
    <div className={`gs-page ${scriptClass} ${loaded ? "gs-loaded" : ""}`}>
      <GurSetuStyles />
      <LaunchBurst burst={burst} />

      {/* ---------------------------------------------------------- */}
      {/* NAV                                                         */}
      {/* ---------------------------------------------------------- */}
      <header className="gs-nav">
        <div className="gs-wordmark">
          <span className="gs-wordmark-dot" aria-hidden="true" />
          GurSetu
        </div>

        <div
          className="gs-lang-switch"
          role="radiogroup"
          aria-label="Choose language / भाषा चुनें / ਭਾਸ਼ਾ ਚੁਣੋ"
        >
          {LANGS.map((code) => (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={lang === code}
              className={`gs-lang-pill ${lang === code ? "gs-lang-pill--active" : ""}`}
              onClick={() => setLang(code)}
              title={LANG_FULL[code]}
            >
              {LANG_LABEL[code]}
            </button>
          ))}
        </div>
      </header>

      {/* ---------------------------------------------------------- */}
      {/* HERO                                                        */}
      {/* ---------------------------------------------------------- */}
      <main>
        <section className="gs-hero">
          <div className="gs-hero-glow" aria-hidden="true" />
          <p className="gs-eyebrow">{t.eyebrow}</p>

          <h1 className="gs-headline" aria-live="polite">
            <span
              className={`gs-headline-text gs-headline-text--${phase}`}
              lang={lang}
            >
              {headlineText}
            </span>
          </h1>

          <p className="gs-sub">{t.sub}</p>

          <div className="gs-marquee" aria-label="GurSetu highlights">
            <div className="gs-marquee-track">
              {[...t.highlights, ...t.highlights].map((item, idx) => (
                <span className="gs-marquee-pill" key={`${item}-${idx}`}>
                  <span className="gs-marquee-spark" aria-hidden="true">✦</span>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="gs-cta"
            onClick={handleCtaClick}
          >
            <PlayStoreGlyph />
            <span>{t.cta}</span>
          </button>
        </section>

        {/* ------------------------------------------------------ */}
        {/* QR PANEL                                                 */}
        {/* ------------------------------------------------------ */}
        <section className="gs-qr-section" aria-label={t.qrTitle}>
          <div className="gs-qr-card">
            <ArchFrame>
              <button
                type="button"
                className="gs-qr-hit"
                onClick={handleQrClick}
                onKeyDown={handleQrKeyDown}
                aria-label={`${t.qrTitle}. ${t.cta}`}
              >
                <img
                  src={QR_IMAGE_SRC}
                  alt="GurSetu Play Store QR code"
                  className="gs-qr-img"
                  draggable="false"
                />
                <span className="gs-qr-ring" aria-hidden="true" />
              </button>
            </ArchFrame>

            <div className="gs-qr-copy">
              <h2 className="gs-qr-title">{t.qrTitle}</h2>
              <p className="gs-qr-sub">{t.qrSub}</p>

              <div className="gs-divider">
                <span />
                <em>{t.orDivider}</em>
                <span />
              </div>

              <button
                type="button"
                className="gs-cta gs-cta--ghost"
                onClick={handleCtaClick}
              >
                <PlayStoreGlyph />
                <span>{t.cta}</span>
              </button>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ */}
        {/* FEATURES                                                 */}
        {/* ------------------------------------------------------ */}
        <section className="gs-features">
          {t.features.map((f, i) => (
            <article className="gs-feature" key={`${f.title}-${i}`}>
              <div className="gs-feature-top">
                <span className="gs-feature-index" aria-hidden="true">
                  <FeatureGlyph index={i} />
                </span>

                <span className="gs-feature-pill">{f.pill}</span>
              </div>

              <h3>{f.title}</h3>
              <p>{f.body}</p>

              <div className="gs-feature-bottom">
                <span className="gs-feature-mini">{f.mini}</span>
                <span className="gs-feature-arrow" aria-hidden="true">→</span>
              </div>

              <span className="gs-feature-shine" aria-hidden="true" />
            </article>
          ))}
        </section>
      </main>

      {/* ---------------------------------------------------------- */}
      {/* FOOTER                                                      */}
      {/* ---------------------------------------------------------- */}
      <footer className="gs-footer">
        <p>{t.footerTag}</p>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Icon bits (inline SVG, no external deps)                            */
/* -------------------------------------------------------------------- */

function PlayStoreGlyph() {
  return (
    <svg
      className="gs-play-glyph"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3.6 2.3c-.4.3-.6.8-.6 1.4v16.6c0 .6.2 1.1.6 1.4l.1.1L13 12.5v-.2L3.7 2.2l-.1.1z"
        fill="#00D2FF"
      />
      <path
        d="M16.1 15.6 13 12.5v-.2l3.1-3.1 6.9 3.9c.9.5.9 1.4 0 1.9l-6.9 3.9z"
        fill="#FFCC00"
      />
      <path
        d="M16.1 15.6 13 12.5 3.7 21.8c.4.4 1 .4 1.8 0l10.6-6.2z"
        fill="#FF3B58"
      />
      <path
        d="M16.1 9.2 5.5 3c-.8-.4-1.4-.4-1.8 0L13 12.5l3.1-3.3z"
        fill="#00F076"
      />
    </svg>
  );
}

function FeatureGlyph({ index }) {
  // Three distinct hand-drawn-feeling glyphs tied to gurdwara life,
  // not generic numbered circles.
  const paths = [
    // clock / timings
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>,
    // hands / seva
    <>
      <path d="M4 13c1.5 1.7 3.2 2.5 5 2.5s3.5-.8 5-2.5" />
      <path d="M4 13V8.5" />
      <path d="M14 15.5V8.5" />
      <path d="M9 15.7V6.8" />
    </>,
    // open book / hukamnama
    <>
      <path d="M12 6.5c-1.8-1.2-4-1.5-6-1v11c2 -.5 4.2-.2 6 1 1.8-1.2 4-1.5 6-1v-11c-2-.5-4.2-.2-6 1z" />
      <path d="M12 6.5v11" />
    </>,
  ];
  return (
    <svg
      viewBox="0 0 24 24"
      width="26"
      height="26"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[index % paths.length]}
    </svg>
  );
}

/* -------------------------------------------------------------------- */
/*  Styles                                                              */
/* -------------------------------------------------------------------- */

function GurSetuStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Manrope:wght@400;500;600;700;800&family=Noto+Sans+Devanagari:wght@400;500;600;700&family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap');

      .gs-page {
        --navy-deep: #0B1D2E;
        --ink: #071119;
        --gold-warm: #C9973E;
        --gold-bright: #F0C05A;
        --saffron: #E1662B;
        --cream: #F6EFE2;
        --cream-dim: #C9C2B4;

        position: relative;
        min-height: 100vh;
        background:
          radial-gradient(ellipse 90% 60% at 50% -10%, rgba(201,151,62,0.16), transparent 60%),
          radial-gradient(ellipse 70% 50% at 85% 15%, rgba(225,102,43,0.10), transparent 60%),
          var(--navy-deep);
        color: var(--cream);
        font-family: 'Manrope', system-ui, sans-serif;
        overflow-x: hidden;
        isolation: isolate;
      }

      .gs-page * { box-sizing: border-box; }

      .gs-page.gs-script-hi { font-family: 'Noto Sans Devanagari', 'Manrope', system-ui, sans-serif; }
      .gs-page.gs-script-pa { font-family: 'Noto Sans Gurmukhi', 'Manrope', system-ui, sans-serif; }

      .gs-headline, .gs-wordmark { font-family: 'Fraunces', 'Noto Sans Devanagari', 'Noto Sans Gurmukhi', serif; }

      /* ---------------- NAV ---------------- */
      .gs-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 28px clamp(20px, 5vw, 64px) 0;
        max-width: 1200px;
        margin: 0 auto;
      }
      .gs-wordmark {
        font-size: 1.35rem;
        font-weight: 600;
        letter-spacing: 0.01em;
        display: flex;
        align-items: center;
        gap: 10px;
        color: var(--cream);
      }
      .gs-wordmark-dot {
        width: 9px; height: 9px; border-radius: 50%;
        background: linear-gradient(135deg, var(--gold-bright), var(--saffron));
        box-shadow: 0 0 12px rgba(240,192,90,0.7);
      }
      .gs-lang-switch {
        display: inline-flex;
        gap: 4px;
        padding: 4px;
        border-radius: 999px;
        background: rgba(255,255,255,0.06);
        border: 1px solid rgba(255,255,255,0.10);
        backdrop-filter: blur(6px);
      }
      .gs-lang-pill {
        appearance: none;
        border: none;
        background: transparent;
        color: var(--cream-dim);
        font-family: 'Manrope', sans-serif;
        font-size: 0.85rem;
        font-weight: 700;
        padding: 7px 14px;
        border-radius: 999px;
        cursor: pointer;
        transition: color 0.25s ease, background 0.25s ease, transform 0.15s ease;
      }
      .gs-lang-pill:hover { color: var(--cream); }
      .gs-lang-pill:focus-visible { outline: 2px solid var(--gold-bright); outline-offset: 2px; }
      .gs-lang-pill--active {
        color: var(--ink);
        background: linear-gradient(135deg, var(--gold-bright), var(--gold-warm));
      }
      .gs-lang-pill--active:hover { color: var(--ink); }

      /* ---------------- HERO ---------------- */
      .gs-hero {
        position: relative;
        max-width: 780px;
        margin: 0 auto;
        padding: clamp(64px, 12vh, 120px) 24px 40px;
        text-align: center;
        opacity: 0;
        transform: translateY(14px);
        transition: opacity 0.9s ease, transform 0.9s cubic-bezier(.2,.7,.2,1);
      }
      .gs-loaded .gs-hero { opacity: 1; transform: translateY(0); }

      .gs-hero-glow {
        position: absolute;
        inset: -20% -10% auto -10%;
        height: 420px;
        background: radial-gradient(ellipse at center, rgba(240,192,90,0.14), transparent 65%);
        pointer-events: none;
        z-index: -1;
      }

      .gs-eyebrow {
        margin: 0 0 18px;
        font-size: 0.95rem;
        font-weight: 600;
        color: var(--gold-bright);
        letter-spacing: 0.02em;
      }

      .gs-headline {
        margin: 0 0 22px;
        min-height: 2.6em;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: clamp(2rem, 5.2vw, 3.4rem);
        font-weight: 600;
        line-height: 1.15;
        color: var(--cream);
      }
      .gs-headline-text {
        display: inline-block;
        filter: blur(0px);
        opacity: 1;
        transform: scale(1);
        transition: filter 0.5s ease, opacity 0.5s ease, transform 0.5s ease;
        will-change: filter, opacity, transform;
      }
      .gs-headline-text--in {
        filter: blur(10px);
        opacity: 0;
        transform: scale(1.03);
        animation: gsHeadlineIn 0.5s ease forwards;
      }
      .gs-headline-text--out {
        filter: blur(10px);
        opacity: 0;
        transform: scale(0.97);
      }
      .gs-headline-text--settled {
        filter: blur(0px);
        opacity: 1;
        transform: scale(1);
      }
      @keyframes gsHeadlineIn {
        from { filter: blur(10px); opacity: 0; transform: scale(1.03); }
        to   { filter: blur(0px);  opacity: 1; transform: scale(1); }
      }
      @media (prefers-reduced-motion: reduce) {
        .gs-headline-text, .gs-headline-text--in, .gs-headline-text--out, .gs-headline-text--settled {
          filter: none; opacity: 1; transform: none; animation: none; transition: none;
        }
      }

      .gs-sub {
        margin: 0 auto 34px;
        max-width: 52ch;
        font-size: 1.05rem;
        line-height: 1.65;
        color: var(--cream-dim);
      }

      /* ---------------- CTA BUTTON ---------------- */
      .gs-cta {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 15px 28px;
        border-radius: 14px;
        border: none;
        cursor: pointer;
        font-family: 'Manrope', sans-serif;
        font-size: 1rem;
        font-weight: 700;
        color: var(--ink);
        background: linear-gradient(135deg, var(--gold-bright), var(--gold-warm));
        box-shadow: 0 10px 30px -8px rgba(201,151,62,0.55);
        transition: transform 0.2s cubic-bezier(.2,.8,.3,1.3), box-shadow 0.2s ease, filter 0.2s ease;
        overflow: hidden;
      }
      .gs-cta:hover { transform: translateY(-2px) scale(1.015); box-shadow: 0 14px 34px -8px rgba(201,151,62,0.7); }
      .gs-cta:active { transform: translateY(0) scale(0.98); }
      .gs-cta:focus-visible { outline: 2px solid var(--cream); outline-offset: 3px; }

      .gs-cta--ghost {
        background: transparent;
        color: var(--cream);
        border: 1.5px solid rgba(240,192,90,0.55);
        box-shadow: none;
      }
      .gs-cta--ghost:hover {
        background: rgba(240,192,90,0.08);
        box-shadow: 0 8px 24px -10px rgba(240,192,90,0.4);
      }

      .gs-play-glyph { flex-shrink: 0; }

      /* ---------------- QR SECTION ---------------- */
      .gs-qr-section {
        max-width: 1000px;
        margin: 20px auto 0;
        padding: 20px 24px 40px;
        opacity: 0;
        transform: translateY(18px);
        transition: opacity 0.9s ease 0.15s, transform 0.9s cubic-bezier(.2,.7,.2,1) 0.15s;
      }
      .gs-loaded .gs-qr-section { opacity: 1; transform: translateY(0); }

      .gs-qr-card {
        display: grid;
        grid-template-columns: minmax(200px, 320px) 1fr;
        align-items: center;
        gap: clamp(28px, 5vw, 64px);
        padding: clamp(28px, 4vw, 48px);
        border-radius: 28px;
        background: linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015));
        border: 1px solid rgba(240,192,90,0.18);
        box-shadow: 0 30px 60px -30px rgba(0,0,0,0.6);
      }

      .gs-arch-frame {
        position: relative;
        width: 100%;
        max-width: 320px;
        margin: 0 auto;
        aspect-ratio: 320 / 360;
      }
      .gs-arch-svg { position: absolute; inset: 0; width: 100%; height: 100%; }
      .gs-arch-content {
        position: absolute;
        top: 15%; left: 9%; right: 9%; bottom: 3%;
        display: flex; align-items: center; justify-content: center;
      }

      .gs-qr-hit {
        position: relative;
        appearance: none;
        border: none;
        padding: 0;
        cursor: pointer;
        border-radius: 18px;
        background: var(--cream);
        width: 100%;
        aspect-ratio: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.25s cubic-bezier(.2,.8,.3,1.3);
      }
      .gs-qr-hit:hover { transform: scale(1.035); }
      .gs-qr-hit:active { transform: scale(0.97); }
      .gs-qr-hit:focus-visible { outline: 3px solid var(--gold-bright); outline-offset: 4px; }

      .gs-qr-img {
        width: 82%;
        height: 82%;
        object-fit: contain;
        border-radius: 8px;
      }

      .gs-qr-ring {
        position: absolute;
        inset: -8px;
        border-radius: 22px;
        border: 2px solid var(--gold-bright);
        opacity: 0;
        animation: gsQrPulse 2.6s ease-in-out infinite;
      }
      @keyframes gsQrPulse {
        0%   { opacity: 0.55; transform: scale(1); }
        70%  { opacity: 0; transform: scale(1.06); }
        100% { opacity: 0; transform: scale(1.06); }
      }
      @media (prefers-reduced-motion: reduce) {
        .gs-qr-ring { animation: none; opacity: 0; }
      }

      .gs-qr-copy { text-align: left; }
      .gs-qr-title {
        margin: 0 0 8px;
        font-family: 'Fraunces', 'Noto Sans Devanagari', 'Noto Sans Gurmukhi', serif;
        font-size: clamp(1.5rem, 2.6vw, 2rem);
        font-weight: 600;
        color: var(--cream);
      }
      .gs-qr-sub {
        margin: 0 0 22px;
        color: var(--cream-dim);
        font-size: 1rem;
      }
      .gs-divider {
        display: flex;
        align-items: center;
        gap: 14px;
        margin: 0 0 22px;
        color: var(--cream-dim);
        font-size: 0.85rem;
      }
      .gs-divider span { flex: 1; height: 1px; background: rgba(255,255,255,0.14); }
      .gs-divider em { font-style: normal; }

      /* ---------------- FEATURES ---------------- */
      .gs-features {
        max-width: 1000px;
        margin: 20px auto 0;
        padding: 40px 24px 30px;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 28px;
        opacity: 0;
        transform: translateY(18px);
        transition: opacity 0.9s ease 0.3s, transform 0.9s cubic-bezier(.2,.7,.2,1) 0.3s;
      }
      .gs-loaded .gs-features { opacity: 1; transform: translateY(0); }

      .gs-feature {
        padding: 26px 22px;
        border-radius: 18px;
        background: rgba(255,255,255,0.03);
        border: 1px solid rgba(255,255,255,0.08);
      }
      .gs-feature-index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 44px; height: 44px;
        border-radius: 12px;
        margin-bottom: 16px;
        color: var(--gold-bright);
        background: rgba(240,192,90,0.10);
      }
      .gs-feature h3 {
        margin: 0 0 8px;
        font-size: 1.05rem;
        font-weight: 700;
        color: var(--cream);
      }
      .gs-feature p {
        margin: 0;
        font-size: 0.92rem;
        line-height: 1.6;
        color: var(--cream-dim);
      }


      /* ---------------- ANIMATED HIGHLIGHTS MARQUEE ---------------- */
      .gs-marquee {
        position: relative;
        margin: -8px auto 30px;
        width: min(100%, 860px);
        overflow: hidden;
        mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
        -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
      }

      .gs-marquee::before,
      .gs-marquee::after {
        content: "";
        position: absolute;
        top: 50%;
        z-index: 2;
        width: 34px;
        height: 34px;
        border-radius: 50%;
        background: rgba(240,192,90,0.08);
        filter: blur(16px);
        transform: translateY(-50%);
        pointer-events: none;
      }

      .gs-marquee::before { left: -10px; }
      .gs-marquee::after { right: -10px; }

      .gs-marquee-track {
        display: flex;
        align-items: center;
        gap: 12px;
        width: max-content;
        padding: 5px 0;
        animation: gsMarquee 22s linear infinite;
        will-change: transform;
      }

      .gs-marquee-pill {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        white-space: nowrap;
        padding: 10px 16px;
        border-radius: 999px;
        background:
          linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.025));
        border: 1px solid rgba(240,192,90,0.18);
        color: var(--cream);
        font-size: 0.88rem;
        font-weight: 700;
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.04),
          0 8px 22px rgba(0,0,0,0.12);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }

      .gs-marquee-spark {
        color: var(--gold-bright);
        text-shadow: 0 0 12px rgba(240,192,90,0.55);
        animation: gsSparkTwinkle 1.8s ease-in-out infinite;
      }

      @keyframes gsMarquee {
        from { transform: translateX(0); }
        to   { transform: translateX(calc(-50% - 6px)); }
      }

      @keyframes gsSparkTwinkle {
        0%, 100% { opacity: 0.55; transform: scale(0.9) rotate(0deg); }
        50%      { opacity: 1; transform: scale(1.2) rotate(15deg); }
      }

      /* ---------------- PREMIUM FEATURE CARDS ---------------- */
      .gs-feature {
        position: relative;
        isolation: isolate;
        overflow: hidden;
        padding: 26px 22px 22px;
        border-radius: 22px;
        background:
          radial-gradient(circle at 100% 0%, rgba(240,192,90,0.08), transparent 34%),
          linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.022));
        border: 1px solid rgba(255,255,255,0.085);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.03),
          0 18px 44px -30px rgba(0,0,0,0.78);
        transition:
          transform 0.3s cubic-bezier(.2,.8,.3,1),
          border-color 0.3s ease,
          box-shadow 0.3s ease,
          background 0.3s ease;
      }

      .gs-feature::before {
        content: "";
        position: absolute;
        inset: 0 auto auto 10%;
        z-index: -1;
        width: 80%;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(240,192,90,0.48), transparent);
        opacity: 0.7;
      }

      .gs-feature:hover {
        transform: translateY(-6px);
        border-color: rgba(240,192,90,0.25);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.045),
          0 24px 52px -30px rgba(0,0,0,0.92),
          0 0 28px rgba(201,151,62,0.07);
      }

      .gs-feature-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 18px;
      }

      .gs-feature-index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 50px;
        height: 50px;
        margin-bottom: 0;
        border-radius: 15px;
        color: var(--gold-bright);
        background:
          radial-gradient(circle at 35% 25%, rgba(240,192,90,0.18), rgba(240,192,90,0.07));
        border: 1px solid rgba(240,192,90,0.15);
        box-shadow:
          inset 0 1px 0 rgba(255,255,255,0.04),
          0 10px 26px rgba(0,0,0,0.12);
      }

      .gs-feature-index svg {
        animation: gsIconBob 3.2s ease-in-out infinite;
        filter: drop-shadow(0 0 10px rgba(240,192,90,0.16));
      }

      .gs-feature:nth-child(2) .gs-feature-index svg { animation-delay: 0.3s; }
      .gs-feature:nth-child(3) .gs-feature-index svg { animation-delay: 0.6s; }

      @keyframes gsIconBob {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50%      { transform: translateY(-4px) rotate(1.5deg); }
      }

      .gs-feature-pill {
        position: relative;
        overflow: hidden;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 30px;
        padding: 7px 12px;
        border-radius: 999px;
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.08em;
        color: var(--ink);
        background: linear-gradient(135deg, var(--gold-bright), var(--gold-warm));
        box-shadow:
          0 0 0 1px rgba(255,255,255,0.05),
          0 0 18px rgba(240,192,90,0.20);
        animation: gsPillPulse 2.3s ease-in-out infinite;
      }

      .gs-feature-pill::after {
        content: "";
        position: absolute;
        inset: 0;
        transform: translateX(-130%) skewX(-18deg);
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.42), transparent);
        animation: gsMiniSweep 3.8s ease-in-out infinite;
      }

      @keyframes gsPillPulse {
        0%, 100% {
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.05),
            0 0 14px rgba(240,192,90,0.14);
        }
        50% {
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.07),
            0 0 25px rgba(240,192,90,0.34);
        }
      }

      @keyframes gsMiniSweep {
        0%, 66%, 100% { transform: translateX(-130%) skewX(-18deg); }
        25%           { transform: translateX(130%) skewX(-18deg); }
      }

      .gs-feature h3 {
        margin: 0 0 10px;
        font-size: 1.08rem;
        font-weight: 800;
        letter-spacing: -0.01em;
        color: var(--cream);
      }

      .gs-feature p {
        margin: 0;
        min-height: 4.8em;
        font-size: 0.94rem;
        line-height: 1.7;
        color: var(--cream-dim);
      }

      .gs-feature-bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 18px;
        padding-top: 14px;
        border-top: 1px solid rgba(255,255,255,0.065);
      }

      .gs-feature-mini {
        min-width: 0;
        font-size: 0.78rem;
        line-height: 1.35;
        color: var(--gold-bright);
        opacity: 0.94;
        font-weight: 700;
      }

      .gs-feature-arrow {
        flex: 0 0 auto;
        font-size: 1.1rem;
        color: var(--gold-bright);
        text-shadow: 0 0 10px rgba(240,192,90,0.35);
        animation: gsArrowNudge 1.35s ease-in-out infinite;
      }

      @keyframes gsArrowNudge {
        0%, 100% { transform: translateX(0); opacity: 0.72; }
        50%      { transform: translateX(4px); opacity: 1; }
      }

      .gs-feature-shine {
        position: absolute;
        z-index: 2;
        top: -45%;
        left: -35%;
        width: 34%;
        height: 190%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255,255,255,0.07),
          rgba(240,192,90,0.08),
          transparent
        );
        transform: rotate(18deg) translateX(-240%);
        animation: gsFeatureShine 5.2s ease-in-out infinite;
        pointer-events: none;
      }

      .gs-feature:nth-child(2) .gs-feature-shine { animation-delay: 0.75s; }
      .gs-feature:nth-child(3) .gs-feature-shine { animation-delay: 1.5s; }

      @keyframes gsFeatureShine {
        0%, 72%, 100% { transform: rotate(18deg) translateX(-240%); }
        24%           { transform: rotate(18deg) translateX(620%); }
      }

      /* ---------------- QR PANEL POLISH ---------------- */
      .gs-qr-card {
        position: relative;
        overflow: hidden;
      }

      .gs-qr-card::before {
        content: "";
        position: absolute;
        inset: 0;
        background:
          radial-gradient(circle at 12% 16%, rgba(240,192,90,0.09), transparent 25%),
          radial-gradient(circle at 88% 12%, rgba(225,102,43,0.08), transparent 22%),
          linear-gradient(115deg, transparent 25%, rgba(255,255,255,0.018) 45%, transparent 64%);
        pointer-events: none;
      }

      .gs-qr-hit {
        box-shadow:
          0 14px 34px rgba(0,0,0,0.20),
          0 0 0 1px rgba(255,255,255,0.14);
      }

      .gs-qr-hit:hover {
        transform: scale(1.04) rotate(-0.35deg);
      }

      /* ---------------- CTA SHINE ---------------- */
      .gs-cta::after {
        content: "";
        position: absolute;
        top: -20%;
        left: -120%;
        width: 70%;
        height: 140%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255,255,255,0.38),
          transparent
        );
        transform: skewX(-18deg);
        animation: gsCtaSweep 3.4s ease-in-out infinite;
        pointer-events: none;
      }

      @keyframes gsCtaSweep {
        0%, 62%, 100% { left: -120%; }
        22%           { left: 145%; }
      }

      @media (prefers-reduced-motion: reduce) {
        .gs-marquee-track,
        .gs-marquee-spark,
        .gs-feature-index svg,
        .gs-feature-pill,
        .gs-feature-pill::after,
        .gs-feature-arrow,
        .gs-feature-shine,
        .gs-cta::after {
          animation: none !important;
        }
      }

      /* ---------------- FOOTER ---------------- */
      .gs-footer {
        text-align: center;
        padding: 30px 24px 44px;
        color: var(--cream-dim);
        font-size: 0.9rem;
      }

      /* ---------------- LAUNCH BURST ---------------- */
      .gs-burst {
        position: fixed;
        z-index: 9999;
        width: 0; height: 0;
        pointer-events: none;
        transform: translate(-50%, -50%);
      }
      .gs-burst-ring {
        position: absolute;
        top: 0; left: 0;
        width: 20px; height: 20px;
        margin: -10px 0 0 -10px;
        border-radius: 50%;
        border: 2px solid var(--gold-bright);
        opacity: 0.9;
        animation: gsBurst 0.62s cubic-bezier(.2,.7,.2,1) forwards;
      }
      .gs-burst-ring--2 { animation-delay: 0.08s; border-color: var(--saffron); }
      .gs-burst-ring--3 { animation-delay: 0.16s; border-color: var(--cream); }
      @keyframes gsBurst {
        to { width: 460px; height: 460px; margin: -230px 0 0 -230px; opacity: 0; }
      }

      /* ---------------- RESPONSIVE ---------------- */
      @media (max-width: 780px) {
        .gs-qr-card { grid-template-columns: 1fr; text-align: center; }
        .gs-qr-copy { text-align: center; }
        .gs-divider { max-width: 260px; margin-left: auto; margin-right: auto; }
        .gs-features { grid-template-columns: 1fr; }
      }

      @media (max-width: 480px) {
        .gs-nav { flex-direction: column; gap: 14px; align-items: center; }
      }
    `}</style>
  );
}